import React, { useState } from 'react';
import { ChevronLeft, Sparkles, Users, Gift, Heart } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GameState, Book, CollectibleObject, UserItemInstance } from '../types';
import { objectsWithTranslations } from '../data/objects';
import { useAppData } from '../hooks/useAppData';
import { useObjects } from '../hooks/useObjects';
import { useHeroes } from '../hooks/useHeroes';
import { useCompanions } from '../hooks/useCompanions';
import { useTranslation } from '../contexts/TranslationContext';
import ItemEffectsRenderer from './ItemEffectsRenderer';

type TabType = 'heroes' | 'objects' | 'companions';

interface CollectionScreenProps {
  gameState: GameState;
  allObjects: CollectibleObject[];
  userInstances: UserItemInstance[];
  books: Book[];
  onBack: () => void;
  onBookRead: (book: Book) => void;
  onObjectToHome: (instanceId: number) => void;
  onHeroSelect: (heroId: number) => void;
}

const CollectionScreen: React.FC<CollectionScreenProps> = ({
  gameState,
  allObjects: legacyObjects,
  userInstances,
  books,
  onBack,
  onBookRead,
  onObjectToHome,
  onHeroSelect
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('heroes');
  const { collectedObjects, homeObjects, heroes, selectedHero } = gameState;
  const { collectionTexts, heroesTexts, nav, translate } = useAppData();
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();

  // Load data from Supabase
  const { objects: gameObjects, loading: objectsLoading, error: objectsError } = useObjects();
  const { heroes: gameHeroes, loading: heroesLoading, error: heroesError } = useHeroes();
  const { companions: gameCompanions, loading: companionsLoading, error: companionsError } = useCompanions();

  // Use Supabase data or fallback
  const allObjects = gameObjects.length > 0 ? gameObjects : legacyObjects;
  const allHeroes = (gameHeroes.length > 0 ? gameHeroes : heroes)
    .filter(hero => hero.hasBeenPublished || hero.unlocked);
  const allCompanions = gameCompanions.filter(c => c.hasBeenPublished || c.unlocked);

  // Calculate progress for each category
  const publishedObjects = allObjects.filter(obj => obj.hasBeenPublished);
  const publishedObjectIds = new Set(publishedObjects.map(obj => obj.id));
  const uniqueCollectedObjectIds = new Set(
    userInstances
      .filter(inst => !inst.archived)
      .filter(inst => publishedObjectIds.has(inst.baseElementId))
      .map(inst => inst.baseElementId)
  );
  const collectedObjectsCount = uniqueCollectedObjectIds.size;
  const totalObjects = publishedObjects.length;

  const unlockedHeroesCount = allHeroes.filter(hero => hero.unlocked).length;
  const totalHeroes = allHeroes.length;

  const unlockedCompanionsCount = allCompanions.filter(c => c.unlocked).length;
  const totalCompanions = allCompanions.length;

  // Group instances by baseElementId AND appliedEffects
  const instanceGroups = new Map<string, UserItemInstance[]>();
  userInstances
    .filter(inst => !inst.archived)
    .forEach(inst => {
      const effectsKey = (inst.appliedEffects || []).sort().join(',');
      const groupKey = `${inst.baseElementId}:${effectsKey}`;
      if (!instanceGroups.has(groupKey)) {
        instanceGroups.set(groupKey, []);
      }
      instanceGroups.get(groupKey)!.push(inst);
    });

  const placedInstanceIds = new Set(
    homeObjects.map(obj => obj.instanceId).filter(id => id !== undefined)
  );

  const isLoading = objectsLoading || heroesLoading || companionsLoading;
  const error = objectsError || heroesError || companionsError;

  const tabs: { id: TabType; icon: React.ReactNode; label: string; count: string; gradient: string }[] = [
    { id: 'heroes', icon: <Users size={18} />, label: t("collection.heroes", "Heroes"), count: `${unlockedHeroesCount}/${totalHeroes}`, gradient: 'from-purple-400 to-pink-500' },
    { id: 'objects', icon: <Gift size={18} />, label: t("collection.objects", "Objects"), count: `${collectedObjectsCount}/${totalObjects}`, gradient: 'from-blue-400 to-purple-500' },
    { id: 'companions', icon: <Heart size={18} />, label: t("collection.companions", "Companions"), count: `${unlockedCompanionsCount}/${totalCompanions}`, gradient: 'from-pink-400 to-red-500' },
  ];

  const currentTab = tabs.find(t => t.id === activeTab)!;

  // Calculate progress percentage for current tab
  const getProgressPercentage = () => {
    switch (activeTab) {
      case 'heroes': return totalHeroes > 0 ? (unlockedHeroesCount / totalHeroes) * 100 : 0;
      case 'objects': return totalObjects > 0 ? (collectedObjectsCount / totalObjects) * 100 : 0;
      case 'companions': return totalCompanions > 0 ? (unlockedCompanionsCount / totalCompanions) * 100 : 0;
    }
  };

  const renderHeroesTab = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {allHeroes.map(hero => {
        const isSelected = selectedHero === hero.id;
        const booksWithHero = books.filter(b => b.objects?.includes(hero.id));

        let sourceText = '';
        if (hero.unlocked) {
          sourceText = '🎁 ' + t("collection.heros_par_defaut", "Default hero");
        } else if (booksWithHero.length === 0) {
          sourceText = '❓ ' + t("collection.source_inconnue", "Unknown source");
        } else if (booksWithHero.length === 1) {
          sourceText = `📖 ${booksWithHero[0].title}`;
        } else {
          sourceText = `📖 ${booksWithHero.length} ${t("collection.livres", "books")}`;
        }

        return (
          <div
            key={hero.id}
            className={`bg-white rounded-lg p-4 shadow-md border-2 transition-all duration-200 cursor-pointer hover:shadow-lg ${
              isSelected
                ? 'border-yellow-400 ring-2 ring-yellow-200'
                : hero.unlocked
                  ? 'border-green-400 hover:border-green-500'
                  : 'border-gray-200 hover:border-blue-300'
            }`}
            onClick={() => hero.unlocked && onHeroSelect(hero.id)}
          >
            <div className="text-center">
              <div className={`mb-2 relative ${hero.unlocked ? 'opacity-100' : 'opacity-30 grayscale scale-90'}`}>
                <img src={hero.emoji} alt={hero.name} className="w-16 h-16 mx-auto object-contain" />
                {isSelected && (
                  <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full w-5 h-5 flex items-center justify-center">
                    <span className="text-xs">⭐</span>
                  </div>
                )}
              </div>
              <h3 className={`font-semibold text-sm mb-1 ${hero.unlocked ? 'text-gray-900' : 'text-gray-400'}`}>
                {hero.name}
              </h3>
              {hero.unlocked ? (
                <p className="text-xs text-green-600">✅ {isSelected ? t("collection.selectionne", "Selected") : t("collection.debloque", "Unlocked")}</p>
              ) : (
                <p className="text-xs text-gray-400">{sourceText}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderObjectsTab = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {/* Collected objects */}
      {Array.from(instanceGroups.entries()).map(([groupKey, instances]) => {
        const firstInstance = instances[0];
        const object = allObjects.find(obj => obj.id === firstInstance.baseElementId);
        if (!object) return null;

        const objectData = objectsWithTranslations.find(o => o.id === object.id);
        const hasEffects = firstInstance.appliedEffects && firstInstance.appliedEffects.length > 0;
        const quantity = instances.length;
        const availableInstance = instances.find(inst => !placedInstanceIds.has(inst.id));

        return (
          <div key={groupKey} className="bg-white rounded-lg p-4 shadow-md border-2 border-green-400 hover:border-green-500 transition-all">
            <div className="text-center">
              <div className="mb-2 relative">
                <img
                  src={object.emoji}
                  alt={objectData ? translate(objectData.translations, 'name') : object.name}
                  className={`w-16 h-16 mx-auto object-contain ${hasEffects ? 'drop-shadow-[0_0_8px_rgba(255,215,0,0.8)]' : ''}`}
                />
                {hasEffects && (
                  <>
                    <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs px-1.5 rounded-full flex items-center z-20">
                      <Sparkles size={10} className="mr-0.5" />
                      {firstInstance.appliedEffects.length}
                    </div>
                    <div className="absolute inset-0 pointer-events-none">
                      <ItemEffectsRenderer effects={firstInstance.appliedEffects} size="medium" intensity="medium" />
                    </div>
                  </>
                )}
              </div>
              <h3 className="font-semibold text-sm mb-1 text-gray-900">
                {objectData ? translate(objectData.translations, 'name') : object.name}
              </h3>
              <p className="text-xs text-green-600">
                ✅ {t("collection.collecte", "Collected")}
                {quantity > 1 && ` (x${quantity})`}
              </p>
              {availableInstance && (
                <button
                  onClick={() => onObjectToHome(availableInstance.id)}
                  className="mt-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded hover:bg-blue-200"
                >
                  {t("collection.ajouter_au_decor", "Add to decoration")}
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Non-collected objects */}
      {publishedObjects
        .filter(object => !uniqueCollectedObjectIds.has(object.id))
        .map(object => {
          const booksWithObject = books.filter(b => b.objects?.includes(object.id));
          const firstBook = booksWithObject[0];
          const objectData = objectsWithTranslations.find(o => o.id === object.id);

          let sourceText = booksWithObject.length === 0
            ? '❓ ' + t("collection.source_inconnue", "Unknown source")
            : booksWithObject.length === 1
              ? `📖 ${booksWithObject[0].title}`
              : `📖 ${booksWithObject.length} ${t("collection.livres", "books")}`;

          return (
            <div
              key={object.id}
              className="bg-white rounded-lg p-4 shadow-md border-2 border-gray-200 hover:border-blue-300 cursor-pointer"
              onClick={() => {
                if (firstBook) {
                  const objectName = objectData ? translate(objectData.translations, 'name') : object.name;
                  if (window.confirm(collectionTexts.getConfirmReadText(firstBook.title, objectName))) {
                    onBookRead(firstBook);
                  }
                }
              }}
            >
              <div className="text-center">
                <div className="mb-2 opacity-30 grayscale scale-90">
                  <img
                    src={object.emoji}
                    alt={objectData ? translate(objectData.translations, 'name') : object.name}
                    className="w-16 h-16 mx-auto object-contain"
                  />
                </div>
                <h3 className="font-semibold text-sm mb-1 text-gray-400">
                  {objectData ? translate(objectData.translations, 'name') : object.name}
                </h3>
                <p className="text-xs text-gray-400">{sourceText}</p>
              </div>
            </div>
          );
        })}
    </div>
  );

  const renderCompanionsTab = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {allCompanions.length === 0 ? (
        <div className="col-span-full text-center py-12">
          <Heart size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">{t("collection.aucun_compagnon_disponible_pour_le_moment", "No companions available yet")}</p>
          <p className="text-gray-400 text-sm mt-2">{t("collection.les_compagnons_apparaitront_ici_quand_vous_les_deb", "Companions will appear here when you unlock them in stories")}</p>
        </div>
      ) : (
        allCompanions.map(companion => {
          const booksWithCompanion = books.filter(b => b.objects?.includes(companion.id));

          let sourceText = '';
          if (companion.unlocked) {
            sourceText = '🎁 ' + t("collection.debloque", "Unlocked");
          } else if (booksWithCompanion.length === 0) {
            sourceText = '❓ ' + t("collection.source_inconnue", "Unknown source");
          } else if (booksWithCompanion.length === 1) {
            sourceText = `📖 ${booksWithCompanion[0].title}`;
          } else {
            sourceText = `📖 ${booksWithCompanion.length} ${t("collection.livres", "books")}`;
          }

          return (
            <div
              key={companion.id}
              className={`bg-white rounded-lg p-4 shadow-md border-2 transition-all ${
                companion.unlocked
                  ? 'border-pink-400 hover:border-pink-500'
                  : 'border-gray-200 hover:border-pink-300'
              }`}
            >
              <div className="text-center">
                <div className={`mb-2 ${companion.unlocked ? 'opacity-100' : 'opacity-30 grayscale scale-90'}`}>
                  <img src={companion.emoji} alt={companion.name} className="w-16 h-16 mx-auto object-contain" />
                </div>
                <h3 className={`font-semibold text-sm mb-1 ${companion.unlocked ? 'text-gray-900' : 'text-gray-400'}`}>
                  {companion.name}
                </h3>
                {companion.unlocked ? (
                  <p className="text-xs text-pink-600">✅ {t("collection.debloque", "Unlocked")}</p>
                ) : (
                  <p className="text-xs text-gray-400">{sourceText}</p>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="h-screen-adaptive bg-gray-50">
      <div className={`bg-white shadow-sm ${isNative ? 'safe-area-top pt-2' : ''}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ChevronLeft size={20} />
              {t("collection.back", "Back")}
            </button>
            <h1 className="text-xl font-bold text-gray-900 font-title">🏆 {t("collection.my_collection", "My Collection")}</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.gradient} text-white shadow-md`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="text-xs opacity-80">({tab.count})</span>
              </button>
            ))}
          </div>

          {/* Progress bar for current tab */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-full max-w-md relative">
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className={`bg-gradient-to-r ${currentTab.gradient} h-4 rounded-full transition-all duration-500`}
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-white drop-shadow-md">
                  {currentTab.count}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-600">{t("collection.loading", "Loading...")}</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-400">⚠️</div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t("collection.erreur_de_chargement", "Loading error")}</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab content */}
        {!isLoading && !error && (
          <>
            {activeTab === 'heroes' && renderHeroesTab()}
            {activeTab === 'objects' && renderObjectsTab()}
            {activeTab === 'companions' && renderCompanionsTab()}
          </>
        )}
      </div>
    </div>
  );
};

export default CollectionScreen;
